import React, { useState, useEffect } from 'react';
import './App.css';
import WordForm from './components/WordForm';
import WordHistory from './components/WordHistory';
import { initSession, getCurrentUser, startGoogleAuth, loadData, saveData, logout } from './services/api';

function App() {
  const [wordPairs, setWordPairs] = useState([]);
  const [todaysWords, setTodaysWords] = useState([]);
  const [canAddToday, setCanAddToday] = useState(true);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Initialize session and check auth status
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        
        // Check for session from URL (after OAuth redirect)
        const hasSession = initSession();
        
        if (hasSession) {
          const userData = await getCurrentUser();
          if (userData) {
            setUser(userData);
            await fetchData();
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error initializing app:', error);
        setLoading(false);
      }
    };
    
    init();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await loadData();
      setWordPairs(data.words || []);
      
      // Check if words were added today
      const today = new Date().toDateString();
      const lastEntry = data.words && data.words.length > 0 ? data.words[data.words.length - 1] : null;
      
      if (lastEntry && lastEntry.date === today) {
        setTodaysWords(lastEntry.words || []);
        setCanAddToday(lastEntry.words.length < 5);
      } else {
        setTodaysWords([]);
        setCanAddToday(true);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = () => {
    startGoogleAuth();
  };

  const handleSignOut = async () => {
    try {
      await logout();
      setUser(null);
      setWordPairs([]);
      setTodaysWords([]);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleAddWord = async (newWord) => {
    try {
      const today = new Date().toDateString();
      const updatedTodaysWords = [...todaysWords, newWord];
      setTodaysWords(updatedTodaysWords);
      
      // Update wordPairs by either adding a new entry for today or updating the existing one
      let updatedWordPairs = [...wordPairs];
      const todayEntryIndex = updatedWordPairs.findIndex(entry => entry.date === today);
      
      if (todayEntryIndex >= 0) {
        // Update existing entry for today
        updatedWordPairs[todayEntryIndex] = {
          ...updatedWordPairs[todayEntryIndex],
          words: updatedTodaysWords
        };
      } else {
        // Add new entry for today
        updatedWordPairs.push({
          date: today,
          words: updatedTodaysWords
        });
      }
      
      setWordPairs(updatedWordPairs);
      
      // Save to Google Drive via our backend
      await saveData({
        words: updatedWordPairs,
        lastAddedDate: today
      });
      
      // Check if we've reached the limit
      if (updatedTodaysWords.length >= 5) {
        setCanAddToday(false);
      }
      
      return true;
    } catch (error) {
      console.error('Error saving word:', error);
      alert('Failed to save word. Please try again.');
      return false;
    }
  };

  if (loading) {
    return <div className="App"><p>Loading...</p></div>;
  }

  return (
    <div className="App">
      <h1>Spanish Word Learning</h1>
      
      {!user ? (
        <div className="login-container">
          <p>Sign in with Google to start learning Spanish words</p>
          <button onClick={handleSignIn} className="google-signin-btn">
            Sign in with Google
          </button>
        </div>
      ) : (
        <>
          <div className="user-info">
            <span>Hello, {user.name}</span>
            <button onClick={handleSignOut} className="signout-btn">Sign Out</button>
          </div>
          
          {canAddToday ? (
            <WordForm 
              onAddWord={handleAddWord} 
              todaysWords={todaysWords} 
              maxWords={5}
            />
          ) : (
            <p>You've already added 5 words today. Come back tomorrow!</p>
          )}
          
          <WordHistory wordPairs={wordPairs} />
        </>
      )}
    </div>
  );
}

export default App; 